import * as React from "react";
import { useCallback } from "react";
import styled from "styled-components";
import trim from "lodash/trim";
import { Field, useFormikContext } from "formik";
import { Input } from "components/bootstrap";
import { ColorPickerPopover, Select } from "components/common";
import { DEFAULT_CUSTOM_HIGHLIGHT_RANGE } from "views/Constants";
import ColorPreview, { GradientColorPreview } from "views/components/sidebar/highlighting/ColorPreview";
import { StaticColor } from "views/logic/views/formatting/highlighting/HighlightingColor";
import { COLORSCALES } from "views/logic/aggregationbuilder/visualizations/HeatmapVisualizationConfig";
import { defaultCompare } from "logic/DefaultCompare";
const StaticColorPicker = () => /* @__PURE__ */ React.createElement(Field, { name: "color.color" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: name, error: meta == null ? void 0 : meta.error, label: "Color" }, /* @__PURE__ */ React.createElement(
  ColorPickerPopover,
  {
    id: "formatting-rule-color",
    placement: "right",
    color: value,
    colors: DEFAULT_CUSTOM_HIGHLIGHT_RANGE.map((c) => [c]),
    triggerNode: /* @__PURE__ */ React.createElement(ColorPreview, { color: StaticColor.create(value) }),
    onChange: (newColor, _, hidePopover) => {
      hidePopover();
      onChange({ target: { name, value: newColor } });
    }
  }
)));
const OptionContainer = styled.div`
  display: flex;
`;
const createOption = (name) => ({
  label: /* @__PURE__ */ React.createElement(OptionContainer, null, /* @__PURE__ */ React.createElement(GradientColorPreview, { $gradient: name }), name),
  value: name
});
const GRADIENTS = [...COLORSCALES].sort(defaultCompare).map(createOption);
const numericRegex = /^-?[0-9]+$/;
const validateColor = (values) => {
  const errors = {};
  if ((values == null ? void 0 : values.type) === "gradient") {
    if (trim(values == null ? void 0 : values.gradient) === "") {
      errors.gradient = "Must be selected.";
    }
    if (trim(values == null ? void 0 : values.lower) === "") {
      errors.lower = "Must be present.";
    } else if (!numericRegex.test(values == null ? void 0 : values.lower)) {
      errors.lower = "Must be a number.";
    }
    if (trim(values == null ? void 0 : values.upper) === "") {
      errors.upper = "Must be present.";
    } else if (!numericRegex.test(values == null ? void 0 : values.upper)) {
      errors.upper = "Must be a number.";
    }
    if ((values == null ? void 0 : values.upper) <= (values == null ? void 0 : values.lower)) {
      errors.upper = "Must be higher than lowest value.";
    }
  }
  return Object.keys(errors).length > 0 ? errors : void 0;
};
const GradientColorPicker = () => /* @__PURE__ */ React.createElement(Field, { name: "color", validate: validateColor }, () => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { name: "color.gradient" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(Input, { id: `${name}-name`, error: meta == null ? void 0 : meta.error, label: "Gradient Name" }, /* @__PURE__ */ React.createElement(
  Select,
  {
    options: GRADIENTS,
    placeholder: "Select gradient colors",
    value,
    onChange: (newGradient) => onChange({ target: { name, value: newGradient } })
  }
))), /* @__PURE__ */ React.createElement(Field, { name: "color.lower" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
  Input,
  {
    id: name,
    placeholder: "Specify lowest value",
    label: "Lowest Value",
    type: "number",
    value,
    error: meta == null ? void 0 : meta.error,
    onChange,
    help: "The lowest value expected in the field/series.",
    required: true
  }
)), /* @__PURE__ */ React.createElement(Field, { name: "color.upper" }, ({ field: { name, value, onChange }, meta }) => /* @__PURE__ */ React.createElement(
  Input,
  {
    id: name,
    "aria-label": "Specify highest value",
    label: "Highest Value",
    type: "number",
    value,
    error: meta == null ? void 0 : meta.error,
    onChange,
    help: "The highest value expected in the field/series.",
    required: true
  }
))));
const ColorForm = ({ type }) => {
  switch (type) {
    case "static":
      return /* @__PURE__ */ React.createElement(StaticColorPicker, null);
    case "gradient":
      return /* @__PURE__ */ React.createElement(GradientColorPicker, null);
    default:
      return null;
  }
};
const Container = styled.div`
  margin-left: 10px;
`;
const randomColor = () => DEFAULT_CUSTOM_HIGHLIGHT_RANGE[Math.floor(Math.random() * DEFAULT_CUSTOM_HIGHLIGHT_RANGE.length)];
export const createNewColor = (newType) => {
  if (newType === "static") {
    return {
      type: "static",
      color: randomColor()
    };
  }
  if (newType === "gradient") {
    return {
      type: "gradient",
      gradient: "Viridis",
      upper: 0,
      lower: 0
    };
  }
  throw new Error(`Invalid color type: ${newType}`);
};
export const validateColoringType = (value, fieldIsNumeric) => {
  if (!value || value === "") {
    return "Coloring is required";
  }
  if (!fieldIsNumeric && value === "gradient") {
    return "A gradient can only be defined for numeric fields.";
  }
  return void 0;
};
const HighlightingColorForm = ({ field }) => {
  var _a, _b;
  const isNumeric = (_b = (_a = field == null ? void 0 : field.type) == null ? void 0 : _a.isNumeric()) != null ? _b : false;
  const { setFieldValue } = useFormikContext();
  const onChangeType = useCallback(
    (e) => {
      const {
        target: { value: newType }
      } = e;
      setFieldValue("color", createNewColor(newType));
    },
    [setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(Field, { name: "color.type", validate: (value) => validateColoringType(value, isNumeric) }, ({ field: { name, value }, meta }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Input, { id: `${name}-coloring`, label: "Coloring", error: meta == null ? void 0 : meta.error }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      checked: value === "static",
      formGroupClassName: "",
      id: name,
      label: "Static Color",
      onChange: onChangeType,
      type: "radio",
      value: "static"
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      checked: value === "gradient",
      formGroupClassName: "",
      id: name,
      disabled: !isNumeric,
      label: "Gradient",
      onChange: onChangeType,
      type: "radio",
      value: "gradient"
    }
  ))), value && /* @__PURE__ */ React.createElement(ColorForm, { type: value })));
};
export default HighlightingColorForm;
